import { Injectable } from '@angular/core';

import { pipe, of } from 'rxjs';
import {
  map,
  take,
  mergeMap,
  concatMap,
  switchMap,
  withLatestFrom,
  catchError
} from 'rxjs/operators';

import { createEffect, ofType, Actions } from '@ngrx/effects';
import { RouterReducerState } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';

import { RouterStateUrl } from '@sd-portal/shared/util';

import { State } from '..';
import { TripManagerAuditService } from '../../trip-manager-audit.service';
import * as actions from './trip-manager.actions';

/**
 * router integration
 */
export const MAP_ROUTER_STORE = pipe(
  select((s: { router: RouterReducerState<RouterStateUrl> }) => s.router),
  map(r => r && r.state && r.state.params),
  take(1)
);

@Injectable()
export class TripManagerEffects {
  constructor(
    private readonly tripManagerAuditService: TripManagerAuditService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  // Skeleton
  loadPostedAircraftTripSkeletons = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedAircraftTripSkeletons),
      mergeMap(() =>
        this.store.pipe(
          MAP_ROUTER_STORE,
          map(params => Number(params.id)),
          switchMap(id =>
            id ? this.tripManagerAuditService.getPostedAircraftTripSkeletons(id) : of([])
          ),
          map(payload => actions.PostedAircraftTripSkeletonsLoaded({ payload })),
          catchError(err => of(actions.PostedAircraftTripSkeletonsLoadError(err)))
        )
      )
    )
  );

  // Posted Aircraft Trip Audit
  loadPostedAircraftTripAudit = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedAircraftTripAudit),
      mergeMap(action =>
        this.tripManagerAuditService.getPostedAircraftTripAudit(action.postedAircraftTripId).pipe(
          map(audits =>
            actions.PostedAircraftTripAuditLoaded({
              postedAircraftTripId: action.postedAircraftTripId,
              audits
            })
          ),
          catchError(err => of(actions.PostedAircraftTripAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePostedAircraftTripAudit = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedAircraftTripAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.tripManager.postedAircraftTripAudit.lastAuditLogId;
        }
        return this.tripManagerAuditService
          .getPostedAircraftTripAudit(
            action.postedAircraftTripId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedAircraftTripAuditLoaded({
                postedAircraftTripId: action.postedAircraftTripId,
                audits
              })
            ),
            catchError(err => of(actions.PostedAircraftTripAuditLoadError(err)))
          );
      })
    )
  );

  // Posted Aircraft Trip Leg Audit
  loadPostedAircraftLegTripAudit = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPostedAircraftTripLegAudit),
      mergeMap(action =>
        this.tripManagerAuditService
          .getPostedAircraftTripLegAudit(action.postedAircraftTripId, action.postedFlightId)
          .pipe(
            map(audits =>
              actions.PostedAircraftTripLegAuditLoaded({
                postedAircraftTripId: action.postedAircraftTripId,
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PostedAircraftTripLegAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPagePostedAircraftTripLegAudit = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePostedAircraftTripLegAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.tripManager.postedAircraftTripLegAudit.lastAuditLogId;
        }
        return this.tripManagerAuditService
          .getPostedAircraftTripLegAudit(
            action.postedAircraftTripId,
            action.postedFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PostedAircraftTripLegAuditLoaded({
                postedAircraftTripId: action.postedAircraftTripId,
                postedFlightId: action.postedFlightId,
                audits
              })
            ),
            catchError(err => of(actions.PostedAircraftTripLegAuditLoadError(err)))
          );
      })
    )
  );
}
